import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  FaArrowLeft,
  FaMapMarkerAlt,
  FaSpinner,
  FaSave,
  FaHome,
  FaCity,
  FaMapPin,
  FaBuilding,
  FaGlobe,
  FaCheckCircle,
  FaLocationArrow,
} from 'react-icons/fa';
import { putData, getData } from '../../../Services/Fetchnodeservices';

const EditAddressScreen = ({ onBack, isDark = false }) => {
  const location = useLocation();
  
  // State
  const [loading, setLoading] = useState(false);
  const [fetchingLocation, setFetchingLocation] = useState(false);
  const [userId, setUserId] = useState(null);
  const [formData, setFormData] = useState({
    addressLine: '',
    city: '',
    state: '',
    pincode: '',
    landmark: '',
    latitude: 0,
    longitude: 0,
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [successMessage, setSuccessMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // 🎨 ZAPTODOOR PREMIUM ORANGE THEME
  const theme = {
    bg: isDark ? '#0b1120' : '#fff7ed',
    cardBg: isDark ? '#1e293b' : '#ffffff',
    text: isDark ? '#f8fafc' : '#0f172a',
    textSecondary: isDark ? '#94a3b8' : '#64748b',
    border: isDark ? 'rgba(249, 115, 22, 0.15)' : '#fed7aa',
    inputBg: isDark ? '#0f172a' : '#ffffff',
    inputText: isDark ? '#f8fafc' : '#0f172a',
    primary: '#f97316',
    primaryLight: '#fb923c',
    primaryDark: '#ea580c',
    primaryGradient: isDark 
      ? 'linear-gradient(135deg, #ea580c 0%, #f97316 50%, #fb923c 100%)'
      : 'linear-gradient(135deg, #f97316 0%, #fb923c 50%, #fdba74 100%)',
    success: '#16a34a',
    danger: '#dc2626',
    shadow: isDark 
      ? '0 10px 40px rgba(0, 0, 0, 0.5)' 
      : '0 10px 40px rgba(249, 115, 22, 0.12)',
    shadowSm: isDark 
      ? '0 4px 12px rgba(0, 0, 0, 0.3)' 
      : '0 4px 12px rgba(249, 115, 22, 0.08)',
  };

  // Load user data from localStorage
  useEffect(() => {
    const loadUserData = async () => {
      try {
        const passedData = location.state?.addressData;
        if (passedData) {
          setUserId(passedData.restaurantId);
          setFormData({
            addressLine: passedData.address || '',
            city: passedData.city || '',
            state: passedData.state || '',
            pincode: String(passedData.postal || ''),
            landmark: passedData.landmark || '',
            latitude: parseFloat(passedData.latitude) || 0,
            longitude: parseFloat(passedData.longitude) || 0,
          });
          return;
        }

        const userDataStr = localStorage.getItem('userData');
        if (userDataStr) {
          const userData = JSON.parse(userDataStr);
          setUserId(userData.id || userData.restaurantId);
          setFormData({
            addressLine: userData.address || '',
            city: userData.city || '',
            state: userData.state || '',
            pincode: String(userData.postal || ''),
            landmark: userData.landmark || '',
            latitude: parseFloat(userData.latitude) || 0,
            longitude: parseFloat(userData.longitude) || 0,
          });
          return;
        }

        const restaurantId = localStorage.getItem('restaurantId');
        if (restaurantId) setUserId(restaurantId);
      } catch (error) {
        console.error('Error loading user data:', error);
      }
    };
    loadUserData();
  }, [location.state]);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.addressLine.trim()) newErrors.addressLine = 'Required';
    if (!formData.city.trim()) newErrors.city = 'Required';
    if (!formData.state.trim()) newErrors.state = 'Required';
    if (!formData.pincode.trim()) {
      newErrors.pincode = 'Required';
    } else if (!/^[0-9]{6}$/.test(formData.pincode)) {
      newErrors.pincode = 'Invalid (6 digits)';
    }
    if (!formData.latitude || formData.latitude === 0) newErrors.latitude = 'Required';
    if (!formData.longitude || formData.longitude === 0) newErrors.longitude = 'Required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    setTouched({ ...touched, [field]: true });
    if (errors[field]) setErrors({ ...errors, [field]: '' });
    setSuccessMessage('');
  };

  const fetchCurrentLocation = async () => {
    if (!navigator.geolocation) {
      alert('Geolocation is not supported by your browser');
      return;
    }

    setFetchingLocation(true);
    setSuccessMessage('');
    
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        setFormData(prev => ({ ...prev, latitude, longitude }));

        try {
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`
          );
          const data = await response.json();
          
          if (data && data.address) {
            setFormData(prev => ({
              ...prev,
              addressLine: data.display_name?.split(',')[0] || prev.addressLine,
              city: data.address.city || data.address.town || data.address.village || prev.city,
              state: data.address.state || data.address.region || prev.state,
              pincode: data.address.postcode?.replace(/\s/g, '').slice(0, 6) || prev.pincode,
            }));
            setSuccessMessage('Location detected successfully!');
          }
        } catch (error) {
          console.error('Reverse geocoding error:', error);
        }
        setFetchingLocation(false);
        setTimeout(() => setSuccessMessage(''), 4000);
      },
      (error) => {
        setFetchingLocation(false);
        alert('Failed to get GPS location: ' + error.message);
      },
      { enableHighAccuracy: true, timeout: 15000 }
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    setLoading(true);
    setSuccessMessage('');
    
    try {
      const payload = {
        address: formData.addressLine,
        city: formData.city,
        state: formData.state,
        postal: formData.pincode,
        landmark: formData.landmark,
        latitude: String(formData.latitude),
        longitude: String(formData.longitude),
      };

      const response = await putData(`restaurant/Restrunts/update/${userId}`, payload);

      if (response?.status) {
        const userResponse = await getData(`restaurant/Restrunts/${userId}`);
        if (userResponse?.status && userResponse?.data) {
          const updatedUserData = userResponse.data;
          const currentUser = JSON.parse(localStorage.getItem('userData') || '{}');
          const updatedUser = {
            ...currentUser,
            ...updatedUserData,
            address: updatedUserData.address || '',
            city: updatedUserData.city || '',
            state: updatedUserData.state || '',
            postal: updatedUserData.postal || '',
            pincode: updatedUserData.postal || '',
            landmark: updatedUserData.landmark || '',
            latitude: updatedUserData.latitude || '',
            longitude: updatedUserData.longitude || '',
          };
          
          localStorage.setItem('userData', JSON.stringify(updatedUser));
          setFormData({
            addressLine: updatedUserData.address || '',
            city: updatedUserData.city || '',
            state: updatedUserData.state || '',
            pincode: String(updatedUserData.postal || ''),
            landmark: updatedUserData.landmark || '',
            latitude: parseFloat(updatedUserData.latitude) || 0,
            longitude: parseFloat(updatedUserData.longitude) || 0,
          });
          
          setSuccessMessage('✅ Address saved successfully!');
          setTimeout(() => { if (onBack) onBack(); }, 1500);
        }
      } else {
        alert(response?.message || 'Failed to update address');
      }
    } catch (error) {
      console.error('Update address error:', error);
      alert(error?.message || 'Failed to update address');
    } finally {
      setLoading(false);
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    if (onBack) onBack();
    else window.history.back();
  };

  // 🎨 PREMIUM UI STYLES
  const styles = {
    container: {
      minHeight: '100dvh',
      width: '100%',
      fontFamily: "'Inter', 'DM Sans', system-ui, sans-serif",
      backgroundColor: theme.bg,
      backgroundImage: isDark 
        ? 'radial-gradient(circle at 10% 20%, rgba(249, 115, 22, 0.1) 0%, transparent 40%)'
        : 'radial-gradient(circle at 10% 20%, rgba(249, 115, 22, 0.08) 0%, transparent 40%)',
      display: 'flex',
      flexDirection: 'column',
      padding: '20px',
      boxSizing: 'border-box',
      transition: 'background-color 0.3s ease',
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '16px 20px',
      background: theme.cardBg,
      borderRadius: '16px',
      border: `1px solid ${theme.border}`,
      boxShadow: theme.shadowSm,
      marginBottom: '20px',
      flexShrink: 0,
    },
    backButton: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '10px 16px',
      background: isDark ? 'rgba(255,255,255,0.05)' : '#fff7ed',
      border: `1px solid ${theme.border}`,
      borderRadius: '12px',
      color: theme.primary,
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: 600,
      transition: 'all 0.2s ease',
    },
    headerTitle: {
      color: theme.text,
      fontSize: '18px',
      fontWeight: 700,
      margin: 0,
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    },
    form: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      gap: '20px',
      backgroundColor: theme.cardBg,
      borderRadius: '20px',
      padding: '24px',
      border: `1px solid ${theme.border}`,
      boxShadow: theme.shadow,
      overflowY: 'auto',
      transition: 'all 0.3s ease',
    },
    gpsButton: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '10px',
      width: '100%',
      height: '52px',
      border: 'none',
      borderRadius: '14px',
      background: 'linear-gradient(135deg, #16a34a, #15803d)',
      color: '#ffffff',
      fontSize: '15px',
      fontWeight: 700,
      cursor: 'pointer',
      boxShadow: '0 6px 20px rgba(22, 163, 74, 0.3)',
      transition: 'all 0.2s ease',
    },
    successBanner: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      padding: '12px 16px',
      backgroundColor: isDark ? 'rgba(22, 163, 74, 0.15)' : '#f0fdf4',
      border: `1px solid ${isDark ? 'rgba(22, 163, 74, 0.3)' : '#bbf7d0'}`,
      borderRadius: '12px',
      color: theme.success,
      fontSize: '14px',
      fontWeight: 600,
      animation: 'slideDown 0.3s ease',
    },
    coordRow: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '16px',
    },
    coordBox: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '16px 12px',
      backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : '#fff7ed',
      border: `1px solid ${theme.border}`,
      borderRadius: '14px',
      transition: 'all 0.2s ease',
    },
    coordLabel: {
      fontSize: '11px',
      fontWeight: 700,
      color: theme.textSecondary,
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      marginBottom: '6px',
    },
    coordValue: {
      fontSize: '15px',
      fontWeight: 800,
      color: theme.primary,
      fontFamily: "'JetBrains Mono', monospace",
      letterSpacing: '0.5px',
    },
    row: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '16px',
    },
    inputGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
    },
    label: {
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      fontSize: '12px',
      fontWeight: 700,
      color: theme.textSecondary,
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
    },
    inputWrapper: {
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
    },
    inputIcon: {
      position: 'absolute',
      left: '16px',
      color: theme.textSecondary,
      fontSize: '16px',
      pointerEvents: 'none',
      transition: 'color 0.2s ease',
    },
    input: {
      width: '100%',
      height: '52px', // 👈 Premium touch target size
      padding: '0 16px 0 48px',
      borderRadius: '14px',
      border: `2px solid ${theme.border}`,
      backgroundColor: theme.inputBg,
      color: theme.inputText,
      fontSize: '15px',
      fontWeight: 500,
      transition: 'all 0.2s ease',
      boxSizing: 'border-box',
      outline: 'none',
    },
    inputFocus: {
      borderColor: theme.primary,
      boxShadow: `0 0 0 4px ${isDark ? 'rgba(249, 115, 22, 0.15)' : 'rgba(249, 115, 22, 0.1)'}`,
    },
    inputError: {
      borderColor: theme.danger,
      boxShadow: `0 0 0 4px ${isDark ? 'rgba(220, 38, 38, 0.15)' : 'rgba(220, 38, 38, 0.1)'}`,
    },
    errorText: {
      color: theme.danger,
      fontSize: '12px',
      fontWeight: 600,
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
      marginTop: '2px',
    },
    saveButton: {
      width: '100%',
      height: '56px', // 👈 Prominent save button
      border: 'none',
      borderRadius: '16px',
      background: theme.primaryGradient,
      color: '#ffffff',
      fontSize: '16px',
      fontWeight: 700,
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '10px',
      boxShadow: '0 8px 24px rgba(249, 115, 22, 0.35)',
      transition: 'all 0.2s ease',
      marginTop: '8px',
    },
    saveButtonDisabled: {
      opacity: 0.7,
      cursor: 'not-allowed',
      transform: 'none !important',
      boxShadow: 'none',
    },
    requiredBadge: {
      color: theme.danger,
      fontSize: '12px',
      fontWeight: 700,
    },
  };

  const keyframes = `
    @keyframes spin { to { transform: rotate(360deg); } }
    @keyframes slideDown {
      from { opacity: 0; transform: translateY(-8px); }
      to { opacity: 1; transform: translateY(0); }
    }
  `;

  const [focusedField, setFocusedField] = useState(null);

  const renderInput = (id, field, placeholder, icon, type = 'text') => (
    <div style={styles.inputGroup}>
      <label style={styles.label} htmlFor={id}>
        {field === 'landmark' ? <FaBuilding size={14} /> : icon}
        {field.charAt(0).toUpperCase() + field.slice(1)}
        {field !== 'landmark' && <span style={styles.requiredBadge}>*</span>}
      </label>
      <div style={styles.inputWrapper}>
        <div style={{
          ...styles.inputIcon,
          color: focusedField === field ? theme.primary : theme.textSecondary
        }}>
          {icon}
        </div>
        <input
          id={`field-${id}`}
          type={type}
          style={{
            ...styles.input,
            ...(focusedField === field ? styles.inputFocus : {}),
            ...(touched[field] && errors[field] ? styles.inputError : {}),
          }}
          value={formData[field]}
          onChange={(e) => handleChange(field, e.target.value)}
          onFocus={() => setFocusedField(field)}
          onBlur={() => {
            setFocusedField(null);
            setTouched({ ...touched, [field]: true });
          }}
          placeholder={placeholder}
          maxLength={field === 'pincode' ? 6 : undefined}
        />
      </div>
      {touched[field] && errors[field] && (
        <div style={styles.errorText}>
          <FaMapPin size={12} /> {errors[field]}
        </div>
      )}
    </div>
  );

  return (
    <div style={styles.container}>
      <style>{keyframes}</style>

      {/* Header */}
      <div style={styles.header}>
        <button
          style={styles.backButton}
          onClick={handleBack}
          onMouseEnter={(e) => (e.currentTarget.style.background = isDark ? 'rgba(249, 115, 22, 0.15)' : '#ffedd5')}
          onMouseLeave={(e) => (e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.05)' : '#fff7ed')}
        >
          <FaArrowLeft size={14} /> Back
        </button>
        <h1 style={styles.headerTitle}>
          <FaMapMarkerAlt size={18} color={theme.primary} /> Update Location
        </h1>
        <div style={{ width: '70px' }} /> {/* Spacer for centering */}
      </div>

      {/* Form */}
      <form style={styles.form} onSubmit={handleSubmit}>
        {/* GPS Button */}
        <button
          type="button"
          style={styles.gpsButton}
          onClick={fetchCurrentLocation}
          disabled={fetchingLocation}
          onMouseEnter={(e) => {
            if (!fetchingLocation) {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 10px 25px rgba(22, 163, 74, 0.4)';
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 6px 20px rgba(22, 163, 74, 0.3)';
          }}
        >
          {fetchingLocation ? (
            <FaSpinner style={{ animation: 'spin 1s linear infinite' }} size={18} />
          ) : (
            <FaLocationArrow size={18} />
          )}
          {fetchingLocation ? 'Fetching GPS...' : 'Auto-Detect My Location'}
        </button>

        {/* Success Banner */}
        {successMessage && (
          <div style={styles.successBanner}>
            <FaCheckCircle size={16} /> {successMessage}
          </div>
        )}

        {/* Coordinates Display */}
        <div style={styles.coordRow}>
          <div style={styles.coordBox}>
            <div style={styles.coordLabel}><FaGlobe size={12} /> Latitude</div>
            <div style={styles.coordValue}>
              {formData.latitude ? parseFloat(formData.latitude).toFixed(5) : '0.00000'}
            </div>
          </div>
          <div style={styles.coordBox}>
            <div style={styles.coordLabel}><FaGlobe size={12} /> Longitude</div>
            <div style={styles.coordValue}>
              {formData.longitude ? parseFloat(formData.longitude).toFixed(5) : '0.00000'}
            </div>
          </div>
        </div>
        {(errors.latitude || errors.longitude) && (
          <div style={{ ...styles.errorText, justifyContent: 'center', fontSize: '12px' }}>
            <FaMapMarkerAlt size={12} /> Please fetch GPS coordinates
          </div>
        )}

        {/* Address Line */}
        {renderInput('addressLine', 'addressLine', 'Building / Street / Area', <FaHome size={14} />)}

        {/* City & Pincode Row */}
        <div style={styles.row}>
          {renderInput('city', 'city', 'City', <FaCity size={14} />)}
          {renderInput('pincode', 'pincode', '6-digit Pincode', <FaMapPin size={14} />, 'number')}
        </div>

        {/* State & Landmark Row */}
        <div style={styles.row}>
          {renderInput('state', 'state', 'State', <FaGlobe size={14} />)}
          {renderInput('landmark', 'landmark', 'Landmark (Optional)', <FaBuilding size={14} />)}
        </div>

        {/* Save Button - Fixed at bottom */}
        <button
          type="submit"
          style={{
            ...styles.saveButton,
            ...(loading || isSubmitting ? styles.saveButtonDisabled : {}),
          }}
          disabled={loading || isSubmitting}
          onMouseEnter={(e) => {
            if (!loading && !isSubmitting) e.currentTarget.style.transform = 'translateY(-2px)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
          }}
        >
          {loading || isSubmitting ? (
            <FaSpinner style={{ animation: 'spin 1s linear infinite' }} size={18} />
          ) : (
            <FaSave size={18} />
          )}
          {loading || isSubmitting ? 'Saving...' : 'Save Address & GPS'}
        </button>
      </form>
    </div>
  );
};

export default EditAddressScreen;